// loads scripts that need to be on page - every entrypoint should import this!
import { AdLoader, lazyload } from '@datagraphics/cookbook';
import 'intersection-observer';
import { onIntersection } from './utils/button-nav.js';
import { activateDropdown } from './utils/dropdown.js';

document.addEventListener('DOMContentLoaded', () => {
  // Kickstart image lazy loading
  lazyload();
  // Kickstart ads
  const adLoader = new AdLoader({
    adSection: window.AD_SECTION,
  });
  adLoader.init();
});

async function activateCountyDropdown() {
  activateDropdown(document.querySelector('.nav-group'));
}

activateCountyDropdown();

// packages
import { index } from 'd3-array';
import { json } from 'd3-fetch';
import {
  createFeed,
  createOfficeFeed,
  createRaceLookup,
  createRaceStore,
  createRaceFeed,
  createCandidateFeed,
  createCandidateLookup,
  createSubunitLookup,
  indexByApId,
} from './stores.js';
import { topojsonToGeoJson } from './utils/geography.js';
import { renderSvelteComponent } from './utils/render.js';

//components
import TwoColumnList from './components/TwoColumnList.svelte';
import Results from './components/Results.svelte';
import Props from './components/Props.svelte';
import DataTime from './components/DataTime.svelte';
import AtAGlanceContainer from './components/AtAGlanceContainer.svelte';
import Watching from './components/Watching.svelte';
import ResultsMap from './components/ResultsMap.svelte';
import ResultsLegend from './components/ResultsLegend.svelte';
import SideBySide from './components/SideBySide.svelte';

// URL for alpha and pub
import { STATE_URL, GEOGRAPHY_URL, EMBED_URL } from './constants.js';

/**
 * @type {Map<string, import('../types.d').Crosswalk>}
 */
const crosswalk = index(window.CROSSWALK, indexByApId);
//components

async function main() {
  // for live
  const feed = await createFeed(STATE_URL);
  const store = createRaceLookup(feed);

  // map data
  const topology = await json(GEOGRAPHY_URL);
  const [geography, roads, cities] = topojsonToGeoJson(topology, [
    'counties',
    'roads',
    'cities',
  ]);

  const racesOnPage = document.querySelectorAll('.race-result');
  /**  @type {import('./types.d').RaceFeed}  */
  let govRaceFeed;

  for (let idx = 0; idx < racesOnPage.length; idx++) {
    const target = racesOnPage[idx];
    const id = target.id;
    // console.log(id);
    const race = /**  @type {import('./types.d').RaceFeed}  */ (
      createRaceFeed(feed, id)
    );
    if (id === 'CA-G6123-2022-11-08') {
      govRaceFeed = race;
      const selDom = document.getElementById(id);
      const govSideBySide =
        selDom.parentNode.firstElementChild.id === 'governor' ? true : false;
      if (govSideBySide) {
        selDom.classList.add('side-by-side');
        renderSvelteComponent(
          SideBySide,
          /** @type {HTMLElement} */
          (selDom),
          {
            race: race,
            crosswalk,
          }
        );
      } else {
        renderSvelteComponent(
          Results,
          /** @type {HTMLElement} */
          (selDom),
          {
            race: race,
            face: true,
            crosswalk,
          }
        );
      }
    } else if (document.getElementById(id).parentNode.id == 'props') {
      renderSvelteComponent(
        Props,
        /** @type {HTMLElement} */
        (document.getElementById(id)),
        {
          race: race,
        }
      );
    } else {
      renderSvelteComponent(
        Results,
        /** @type {HTMLElement} */
        (document.getElementById(id)),
        {
          race: race,
          face: false,
          crosswalk: '',
        }
      );
    }
  }

  const govRaceCandidateLookup = createCandidateLookup(govRaceFeed);
  const govRaceSubunitLookup = createSubunitLookup(govRaceFeed);

  //MAP
  renderSvelteComponent(
    ResultsMap,
    /** @type {HTMLElement} */
    (document.getElementById('results-map')),
    {
      geography,
      roads,
      cities,
      candidates: govRaceCandidateLookup,
      subunits: govRaceSubunitLookup,
      includeParty: false,
    }
  );
  renderSvelteComponent(
    ResultsLegend,
    /** @type {HTMLElement} */
    (document.getElementById('results-legend')),
    {
      race: govRaceFeed,
    }
  );

  // RACES WE ARE WATCHING
  const embedFeed = await createFeed(EMBED_URL);
  const watchingRace = document.querySelectorAll('.watching');

  for (let idx = 0; idx < watchingRace.length; idx++) {
    const t = watchingRace[idx];
    const watching_id = t.id;
    // console.log(id);
    const watchingFeed = /**  @type {import('./types.d').RaceFeed}  */ (
      createRaceFeed(embedFeed, watching_id)
    );

    renderSvelteComponent(
      Watching,
      /** @type {HTMLElement} */
      (document.getElementById(watching_id)),
      {
        race: watchingFeed,
      }
    );
  }

  ///HOUSE
  const houseFeed = /**  @type {import('./types.d').RaceFeed}  */ (
    createOfficeFeed(feed, 'H')
  );

  renderSvelteComponent(
    TwoColumnList,
    /** @type {HTMLElement} */
    (document.getElementById('House')),
    {
      races: houseFeed,
    }
  );

  ////STATE SENATE
  const stateSenateFeed = /**  @type {import('./types.d').RaceFeed}  */ (
    createOfficeFeed(feed, 'Z')
  );

  renderSvelteComponent(
    TwoColumnList,
    /** @type {HTMLElement} */
    (document.getElementById('StateSenate')),
    {
      races: stateSenateFeed,
    }
  );

  ////STATE ASSEMBLY
  const stateAssemblyFeed = /**  @type {import('./types.d').RaceFeed}  */ (
    createOfficeFeed(feed, 'Y')
  );

  renderSvelteComponent(
    TwoColumnList,
    /** @type {HTMLElement} */
    (document.getElementById('StateAssembly')),
    {
      races: stateAssemblyFeed,
    }
  );

  //AT A GLANCE GOV AND AG
  const atAGlanceRaces = [
    { race: 'governor', id: 'CA-G6123-2022-11-08' },
    { race: 'precincts', id: 'CA-G6123-2022-11-08' },
    { race: 'attorney-general', id: 'CA-A6482-2022-11-08' },
    { race: 'mayor', id: 'CA-M8959-2022-11-08' },
    { race: 'sheriff', id: 'CA-08655-2022-11-08' },
  ];

  atAGlanceRaces.forEach((r) => {
    r['store'] = /** @type {import('./types.d').ReadableRace} */ (
      createRaceFeed(feed, r['id'])
    );
  });

  renderSvelteComponent(
    AtAGlanceContainer,
    /** @type {HTMLElement} */
    (document.getElementById('aag-container')),
    {
      races: atAGlanceRaces,
      crosswalk,
    }
  );

  renderSvelteComponent(
    DataTime,
    /** @type {HTMLElement} */
    (document.getElementById('updated')),
    {
      race: houseFeed,
    }
  );
}

main().catch(console.error);
